import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../cn.js';
import { Avatar } from './Avatar.js';

export interface PersonOption {
  id: string;
  name: string;
  /** Department, designation, or relationship label — rendered under the name in the dropdown. */
  subtitle?: string | null;
}

export interface PersonPickerProps {
  label: string;
  /** Required, `Input`-style — no bare placeholder as the only label (see `Input`'s own doc
   *  comment on why). */
  hint?: string;
  /** The search box's own text. Controlled by the caller so it can drive a debounced
   *  `employees.list({ q })` query the same way any other search field does — this component has
   *  no API client of its own, `@hrms/ui` is server-agnostic. */
  query: string;
  onQueryChange: (query: string) => void;
  /** The current page of matches for `query` — typically `employees.list`'s `items`, already
   *  filtered to exclude anyone already selected by the caller. */
  options: PersonOption[];
  loading?: boolean;
  /** Already-chosen people, rendered as removable chips (or, in `multiple: false` mode, as the
   *  single filled-in value replacing the search box). Carried as full options, not just ids — a
   *  chip must still show a name after its id scrolls out of `options`. */
  selected: PersonOption[];
  onAdd: (option: PersonOption) => void;
  onRemove: (id: string) => void;
  /** Defaults to true (360° respondents, kudos-panel "pick a colleague" — actually a single pick,
   *  see Request360Panel/GiveKudosPanel for which each screen uses). Multi-select shows chips below
   *  the box and keeps it open/re-focused after each pick; single-select closes on pick and shows
   *  the chosen person inline in place of the box. */
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Async person search-and-select — built for `Request360Panel`'s multi-select over peers/managers/
 * reports and reused (single-select mode) by `OneOnOneList`'s "schedule with" field. See the
 * Feedback & 1:1s plan's "Web implementation" section: a second `@hrms/ui` primitive built once, for
 * two Performance Management sections (Section 4's reviewer/participant pickers reuse it too).
 *
 * Deliberately dependency-free, same convention as `DropdownMenu` — no combobox library, a plain
 * text box plus an absolutely-positioned listbox that closes on outside click/Escape.
 */
export function PersonPicker({
  label,
  hint,
  query,
  onQueryChange,
  options,
  loading = false,
  selected,
  onAdd,
  onRemove,
  multiple = true,
  placeholder = 'Search by name or employee code…',
  disabled = false,
}: PersonPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const listId = `${inputId}-listbox`;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const singleValue = !multiple ? (selected[0] ?? null) : null;

  function pick(option: PersonOption) {
    onAdd(option);
    onQueryChange('');
    setOpen(multiple);
  }

  return (
    <div ref={rootRef} className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-fg">
        {label}
      </label>
      {hint && <p className="text-xs text-fg-muted">{hint}</p>}

      {singleValue ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <Avatar name={singleValue.name} size="sm" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-fg">{singleValue.name}</p>
              {singleValue.subtitle && (
                <p className="truncate text-xs text-fg-muted">{singleValue.subtitle}</p>
              )}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(singleValue.id)}
              className="shrink-0 text-xs font-medium text-fg-subtle hover:text-danger"
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            id={inputId}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            value={query}
            disabled={disabled}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              onQueryChange(event.target.value);
              setOpen(true);
            }}
            className={cn(
              'h-[var(--control-height)] w-full rounded-md border border-border bg-surface px-3 text-sm text-fg',
              'placeholder:text-fg-subtle',
              'transition-colors duration-[var(--duration-fast)]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />

          {open && !disabled && (
            <ul
              id={listId}
              role="listbox"
              className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-surface-raised py-1 shadow-lg"
            >
              {loading && <li className="px-3 py-2 text-xs text-fg-subtle">Searching…</li>}
              {!loading && options.length === 0 && (
                <li className="px-3 py-2 text-xs text-fg-subtle">
                  {query ? 'No matches' : 'Start typing a name'}
                </li>
              )}
              {!loading &&
                options.map((option) => (
                  <li key={option.id} role="option" aria-selected={false}>
                    <button
                      type="button"
                      onClick={() => pick(option)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-subtle"
                    >
                      <Avatar name={option.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-fg">{option.name}</span>
                        {option.subtitle && (
                          <span className="block truncate text-xs text-fg-muted">
                            {option.subtitle}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {multiple && selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {selected.map((option) => (
            <li
              key={option.id}
              className="flex items-center gap-1.5 rounded-full border border-border bg-bg-subtle py-1 pl-1 pr-2 text-xs font-medium text-fg"
            >
              <Avatar name={option.name} size="sm" className="size-5 text-[9px]" />
              {option.name}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${option.name}`}
                  onClick={() => onRemove(option.id)}
                  className="text-fg-subtle hover:text-danger"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
